import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { Constants } from '../../../shared/utilities/Constants';
import { selectWireCreditTo } from '../../reducers';
import { createControl } from '../../utilities/formUtilities';
import { FormatIsoAddressFieldsService } from '../../../shared/services/format-isoAddressFields.service';
import { OptionDropdownSelect } from '../../models/OptionDropdownSelect';
import { CountryDescriptionFromCode } from '../../../shared/utilities/CountryDescriptionFromCode';

@Component({
  standalone: false,
  selector: 'mm-wire-credit-to-interim',
  templateUrl: './wire-credit-to-interim.component.html',
  styleUrls: ['./wire-credit-to-interim.component.scss']
})
export class WireCreditToInterimComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() useSloa: boolean;
  @Input() countryList: OptionDropdownSelect[];
  creditToNameHeader: string =  Constants.secondaryIntermediaryNameAddressSubHeader;
  secondIntermediaryUseSloaAddress: string;
  secondIntermediaryAccountNumber: string;
  secondIntermediaryUseSloaAccountNumberHeader: string = "Account number";
  secondIntermediaryBIC: string;
  secondIntermediaryClearingSysId: string;
  isISOCompliant: boolean;

  destroy$: Subject<boolean> = new Subject<boolean>();

  controlNames = {
    name1: 'institutionName',
    name2: 'institutionNameContd',
    accountNumber: 'institutionAccountNumber',
    address1: 'institutionAddress',
    address2: 'institutionAddressContd',
    country: 'institutionCountry',
    phoneNumber: 'institutionPhoneNumber',
  };

  nameModel: TextInputModel = {
    controlName: this.controlNames.name1,
    label: 'Intermediary account name',
    hasGreyText: false,
  };

  nameContdModel: TextInputModel = {
    controlName: this.controlNames.name2,
    label: 'Intermediary account name continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
  };

  accountNumberModel: TextInputModel = {
    controlName: this.controlNames.accountNumber,
    label: 'Intermediary account number',
    hasGreyText: false,
  };

  addressModel: TextInputModel = {
    controlName: this.controlNames.address1,
    label: 'Intermediary address',
    hasGreyText: true,
  };

  addressContdModel: TextInputModel = {
    controlName: this.controlNames.address2,
    label: 'Intermediary address continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
  };

  phoneModel: TextInputModel = {
    controlName: this.controlNames.phoneNumber,
    label: 'Intermediary phone',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
  };

  constructor(private fb: UntypedFormBuilder, private store: Store, private formatIsoAddressFieldsService: FormatIsoAddressFieldsService) {}

  ngOnInit(): void {
    this.isISOCompliant = this.parentForm?.value?.isISOCompliant;
    createControl(this.parentForm, this.controlNames.name1, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.name2, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.accountNumber, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.address1, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.address2, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.country, '', undefined, undefined, true);
    createControl(this.parentForm, this.controlNames.phoneNumber, '', undefined, undefined, true);

    this.store
      .select(selectWireCreditTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_creditTo) => {
        this.institutionName1.setValue(_creditTo.insitutionName1);
        this.institutionName2.setValue(_creditTo.insitutionName2);
        this.institutionAddress.setValue(_creditTo.institutionAddress);
        this.institutionAddressContd.setValue(_creditTo.institutionAddressContd);
        this.institutionCountry.setValue(_creditTo.institutionCountry);
        this.institutionPhoneNumber.setValue(_creditTo.institutionPhoneNumber);
        this.institutionAccountNumber.setValue(_creditTo.insitutionAccountNumber);
      });
    if(this.useSloa) {
      this.secondIntermediaryAccountNumber = (this.parentForm?.value?.secondIntermediaryAccountNumber != null && this.parentForm?.value?.secondIntermediaryAccountNumber != undefined) ? this.parentForm?.value?.secondIntermediaryAccountNumber : 
                                    ((this.parentForm?.value?.secondIntermediaryIBAN != null && this.parentForm?.value?.secondIntermediaryIBAN != undefined) ? this.parentForm?.value?.secondIntermediaryIBAN : null);
      this.secondIntermediaryBIC = this.parentForm?.value?.secondIntermediaryBIC;
      this.secondIntermediaryClearingSysId = this.parentForm?.value?.secondIntermediaryClearingSysId;
      this.secondIntermediaryUseSloaAccountNumberHeader = (this.parentForm?.value?.secondIntermediaryIBAN != null && this.parentForm?.value?.secondIntermediaryIBAN != undefined) ? 'International account number (IBAN)' : this.secondIntermediaryUseSloaAccountNumberHeader; 
      const secondIntermediaryCountry = this.parentForm?.value?.secondIntermediaryCountry?.length == 2 ? CountryDescriptionFromCode(this.parentForm?.value?.secondIntermediaryCountry, this.countryList): this.parentForm?.value?.secondIntermediaryCountry;

      this.secondIntermediaryUseSloaAddress = this.getIntermediarySloaReadOnlyAddress(this.parentForm?.value?.secondIntermediaryName, this.parentForm?.value?.secondIntermediaryStreetNumber, this.parentForm?.value?.secondIntermediaryStreetName, this.parentForm?.value?.secondIntermediaryApartmentSuiteEtc, this.parentForm?.value?.secondIntermediaryDepartment, this.parentForm?.value?.secondIntermediaryBuildingName, this.parentForm?.value?.secondIntermediaryFloor, this.parentForm?.value?.secondIntermediaryTownLocationName, this.parentForm?.value?.secondIntermediaryCity, this.parentForm?.value?.secondIntermediaryDistrict, this.parentForm?.value?.secondIntermediaryState, this.parentForm?.value?.secondIntermediaryZip, secondIntermediaryCountry);
    }
  }

  getIntermediarySloaReadOnlyAddress(name?: string, streetNumber?: string, streetName?: string, apartmentSuiteEtc?: string, department?: string, buildingName?: string, floor?: string, townLocationName?: string, city?: string, district?: string, state?: string, zipCode?: string, country?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getNextLine(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getFieldsValue(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(floor) +
      this.formatIsoAddressFieldsService.getNextLine(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(zipCode) +
      this.formatIsoAddressFieldsService.getNextLine(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country) 
  }

  get institutionName1(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.name1) as UntypedFormControl;
  }

  get institutionName2(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.name2) as UntypedFormControl;
  }

  get institutionAddress(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.address1) as UntypedFormControl;
  }

  get institutionAddressContd(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.address2) as UntypedFormControl;
  }

  get institutionAccountNumber(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.accountNumber) as UntypedFormControl;
  }

  get institutionCountry(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.country) as UntypedFormControl;
  }

  get institutionPhoneNumber(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.phoneNumber) as UntypedFormControl;
  }

  ngOnDestroy(): void {
    Object.keys(this.controlNames).forEach(name => {
      const names = this.controlNames;
      this.parentForm?.removeControl(this.controlNames[name as keyof typeof names]);
    });

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
